/* House of Hours — Watches landing motion (ES module).
 * Mirrors the House of Hours WordPress site:
 *  - Lenis smooth scroll (duration 1.2, easeOutCubic) — same settings as the theme
 *  - Elementor-style entrances: fadeInUp / fadeInDown, 1.25s, per-element delays
 *  - Single-slide collections carousel: autoplay 5s, 500ms slide, loop, arrows + title tabs
 *  - Sticky parallax gallery: images rotate up to data-rotation (10deg) as they scroll past
 *  - Layered images parallax: y -8% / +30% / -30% over the section's scroll range (GSAP smoothing)
 * Libraries load from jsDelivr as modules (no globals touched). If they fail, the page still works. */

const root = document.getElementById('hoh-watches');
// ?hohstatic=1 shows everything without entrance motion (used for design checks).
const staticMode = /[?&]hohstatic=1/.test(location.search);
if (staticMode) document.documentElement.classList.add('hoh-static');
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches || staticMode;

const load = (url) => import(url).catch(() => null);
let lenisRef = null;

/* ---------- Header menu overlay + back-to-top (layout/house-of-hours) ---------- */
(function initChrome() {
  const menu = document.getElementById('hoh-menu');
  const openBtn = document.querySelector('[data-hoh-menu-open]');
  if (menu && openBtn) {
    const closeBtn = menu.querySelector('[data-hoh-menu-close]');
    const open = () => {
      menu.hidden = false;
      requestAnimationFrame(() => menu.classList.add('is-open'));
      openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      if (lenisRef) lenisRef.stop();
      closeBtn && closeBtn.focus();
    };
    const close = () => {
      menu.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      if (lenisRef) lenisRef.start();
      setTimeout(() => { menu.hidden = true; }, 450);
      openBtn.focus();
    };
    openBtn.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !menu.hidden) close(); });
  }

  // Design checks: ?hohstatic=1&hohy=1800 opens the page already scrolled.
  const jump = new URLSearchParams(location.search).get('hohy');
  if (jump) window.addEventListener('load', () => setTimeout(() => window.scrollTo(0, +jump), 300));

  const top = document.querySelector('.hoh-totop');
  if (top) {
    const toggle = () => top.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.6);
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
    top.addEventListener('click', () => (lenisRef ? lenisRef.scrollTo(0) : window.scrollTo({ top: 0, behavior: 'smooth' })));
  }
})();

(async () => {
  if (!root) return;

  /* ---------- Entrance animations ---------- */
  const animEls = [...root.querySelectorAll('[data-hoh-anim]')];
  if (!reduce && 'IntersectionObserver' in window) {
    root.classList.add('hoh-motion');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const el = e.target;
        io.unobserve(el);
        const delay = parseInt(el.getAttribute('data-hoh-delay') || '0', 10);
        if (delay) el.style.animationDelay = delay + 'ms';
        el.classList.add('animated');
        if (window.matchMedia('(hover: none)').matches && el.querySelector('.has-swap')) {
          setTimeout(() => el.classList.add('hoh-peek'), 1400 + delay);
          setTimeout(() => el.classList.remove('hoh-peek'), 3100 + delay);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    animEls.forEach((el) => io.observe(el));
  }

  /* ---------- Carousel ---------- */
  root.querySelectorAll('[data-hoh-carousel]').forEach(initCarousel);

  if (reduce) return;

  /* ---------- Libraries (optional) ---------- */
  const [lenisMod, gsapMod] = await Promise.all([
    root.dataset.smooth === 'true' ? load('https://cdn.jsdelivr.net/npm/lenis@1.1.13/+esm') : null,
    load('https://cdn.jsdelivr.net/npm/gsap@3.12.5/+esm'),
  ]);
  const gsap = gsapMod && (gsapMod.gsap || gsapMod.default);
  const Lenis = lenisMod && lenisMod.default;

  let lenis = null;
  if (Lenis) {
    lenis = lenisRef = new Lenis({
      duration: 1.2,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      // Let the store's own scrollable menus, drawers and popups scroll normally.
      prevent: (node) => !!node.closest && !!node.closest('[data-lenis-prevent], .hoh-menu, .vertical-menu, .ss-vertical-menu, .mm-menu, .modal, .drawer, .sidebar-fixed, [class*="cart"], [class*="popup"], [class*="menu-mobile"], iframe'),
    });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    document.documentElement.classList.add('lenis', 'lenis-smooth');
    // In-page links (Shop now, Discover now) glide like the original.
    root.querySelectorAll('a[href^="#"]').forEach((a) => a.addEventListener('click', (e) => {
      const target = document.querySelector(a.getAttribute('href'));
      if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: -20 }); }
    }));
  }

  const onScroll = (fn) => {
    if (lenis) lenis.on('scroll', () => fn(window.scrollY));
    else window.addEventListener('scroll', () => fn(window.scrollY), { passive: true });
    fn(window.scrollY);
  };
  const lerp = (a, b, p) => a + (b - a) * p;
  const setY = (el, pct) => gsap ? gsap.to(el, { y: pct + '%' }) : (el.style.transform = `translate3d(0, ${pct}%, 0)`);

  /* ---------- Parallax gallery: rotate as it scrolls past ---------- */
  root.querySelectorAll('[data-hoh-parallax]').forEach((sec) => {
    const max = parseFloat(sec.dataset.rotation) || 5;
    const imgs = [...sec.querySelectorAll('.hoh-parallax__img')];
    onScroll(() => {
      const vh = window.innerHeight;
      imgs.forEach((img) => {
        const top = img.getBoundingClientRect().top;
        if (top >= vh) return;
        let deg = Math.min(((vh - top) / vh) * max, max);
        if (img.classList.contains('odd')) deg *= -1;
        requestAnimationFrame(() => { img.style.transform = `translate3d(0,0,0) rotate(${deg}deg)`; });
      });
    });
  });

  /* ---------- Layered images ---------- */
  root.querySelectorAll('[data-hoh-layered]').forEach((box) => {
    const [l1, l2, l3] = ['.hoh-layer--1', '.hoh-layer--2', '.hoh-layer--3'].map((s) => box.querySelector(s));
    if (!l1 || !l2 || !l3) return;
    onScroll((scrollY) => {
      const r = box.getBoundingClientRect();
      const top = scrollY + r.top, bottom = top + r.height, vh = window.innerHeight;
      const start = top - vh * 0.75, end = bottom - vh * 0.25;
      const p = Math.max(0, Math.min(1, (scrollY - start) / (end - start)));
      setY(l1, lerp(0, -8, p)); setY(l2, lerp(0, 30, p)); setY(l3, lerp(0, -30, p));
    });
  });
})();

/* Single-slide carousel: autoplay 5000ms, 500ms slide, loop, arrows, title tabs, swipe. */
function initCarousel(box) {
  const track = box.querySelector('.hoh-swiper-wrapper');
  const slides = [...track.children];
  const tabs = box.querySelector('.hoh-carousel__tabs');
  if (slides.length < 2) { slides[0] && slides[0].classList.add('is-active'); box.classList.add('is-ready'); return; }

  // Clones at both ends for a seamless loop.
  track.prepend(slides[slides.length - 1].cloneNode(true));
  track.append(slides[0].cloneNode(true));
  const all = [...track.children];
  all.forEach((s, i) => { if (i === 0 || i === all.length - 1) s.setAttribute('aria-hidden', 'true'); });

  const SPEED = 500, DELAY = 5000;
  let pos = 1, timer = null, busy = false;

  const tabBtns = slides.map((s, i) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'hoh-carousel__tab';
    b.textContent = s.dataset.title || `Slide ${i + 1}`;
    b.addEventListener('click', () => go(i + 1));
    tabs.appendChild(b);
    return b;
  });

  const paint = (animate) => {
    track.style.transitionDuration = animate ? SPEED + 'ms' : '0ms';
    track.style.transform = `translate3d(${-pos * 100}%, 0, 0)`;
    const real = (pos - 1 + slides.length) % slides.length;
    all.forEach((s, i) => s.classList.toggle('is-active', i === pos));
    tabBtns.forEach((b, i) => {
      b.classList.toggle('is-active', i === real);
      b.setAttribute('aria-current', i === real ? 'true' : 'false');
    });
  };

  function go(to) {
    if (busy || to === pos) return;
    busy = true;
    pos = to;
    tabBtns.forEach((b) => b.classList.remove('is-active')); // restart the tab progress line
    void track.offsetWidth;
    paint(true);
    setTimeout(() => {
      if (pos === all.length - 1) { pos = 1; paint(false); }
      if (pos === 0) { pos = slides.length; paint(false); }
      busy = false;
    }, SPEED + 20);
    restart();
  }
  const next = () => go(pos + 1);
  const prev = () => go(pos - 1);
  const restart = () => { clearInterval(timer); if (!reduce) timer = setInterval(next, DELAY); };

  box.querySelector('.hoh-carousel__next').addEventListener('click', next);
  box.querySelector('.hoh-carousel__prev').addEventListener('click', prev);
  box.addEventListener('mouseenter', () => clearInterval(timer));
  box.addEventListener('mouseleave', restart);

  let x0 = null;
  box.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
  box.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    x0 = null;
  });

  track.style.transitionProperty = 'transform';
  track.style.transitionTimingFunction = 'ease';
  paint(false);
  box.classList.add('is-ready');
  restart();
}
